import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ScreenRouter } from '../../nav';
import { ScreenView } from '../ScreenView';
type LivePhoneProps = {
  path: string;
  label: string;
  className?: string;
  float?: boolean;
};
const SCREEN_WIDTH = 390;
const SCREEN_HEIGHT = 844;
const DARK_ROUTES = ['/splash', '/chat', '/review'];
function shellTone(path: string) {
  const route = path.split('?')[0];
  return DARK_ROUTES.includes(route) ? {
    surface: '#061827',
    text: '#FFFFFF'
  } : {
    surface: '#F4F7FB',
    text: '#061827'
  };
}

/**
 * Mounts a real VE Bank app screen as a live DOM preview inside a device shell.
 *
 * The screen always renders at its true 390x844 design size and is uniformly
 * scaled down to fit the shell, so type, spacing and touch targets keep their
 * designed proportions instead of being squashed by a narrower container.
 *
 * The preview is inert: it is exposed to assistive tech as a single labelled
 * image, and its interactive descendants are removed from the tab order so the
 * landing page never traps focus or duplicates app controls.
 */
export function LivePhone({
  path,
  label,
  className = '',
  float = false
}: LivePhoneProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const node = viewportRef.current;
    if (!node) return;
    const measure = () => setScale(node.clientWidth / SCREEN_WIDTH);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const node = stageRef.current;
    if (!node) return;
    node.scrollTop = 0;
    const lockScroll = () => {
      if (node.scrollTop !== 0) node.scrollTop = 0;
    };
    node.addEventListener('scroll', lockScroll, {
      passive: true
    });
    const seal = () => {
      ;
      (node as HTMLDivElement & {
        inert?: boolean;
      }).inert = true;
      node.querySelectorAll<HTMLElement>('button, a, input, select, textarea, [tabindex]').forEach(element => {
        element.setAttribute('tabindex', '-1');
      });
    };
    seal();
    const observer = new MutationObserver(seal);
    observer.observe(node, {
      childList: true,
      subtree: true
    });
    return () => {
      observer.disconnect();
      node.removeEventListener('scroll', lockScroll);
    };
  }, []);
  const tone = shellTone(path);
  return <figure role="img" aria-label={label} className={`relative m-0 ${className}`} data-id="element-92" data-mp="18911acgwy6n0">
      <div className={`rounded-[45px] bg-[#04111d] p-[7px] shadow-[0_38px_90px_-38px_rgba(6,24,39,.6)] ${float ? 'float-slow' : ''}`} data-id="element-93" data-mp="13brel4kws92v">
        <div ref={viewportRef} className="relative w-full overflow-hidden rounded-[38px]" style={{
        aspectRatio: `${SCREEN_WIDTH} / ${SCREEN_HEIGHT}`,
        background: tone.surface
      }} data-id="element-94" data-mp="1h1uwjjcr9gmi">
          <div className="pointer-events-none absolute inset-x-0 top-0 z-50 flex justify-center" style={{
          height: 26 * scale
        }} data-id="element-95" data-mp="1qyg2c6wlmvre">
            <span className="rounded-b-[16px] bg-[#04111d]" style={{
            height: 26 * scale,
            width: 108 * scale
          }} data-id="element-96" data-mp="25niu69u5c71n" />
          </div>
          <div className="pointer-events-none absolute inset-x-0 top-0 z-40 flex items-center justify-between font-extrabold" style={{
          height: 40 * scale,
          paddingInline: 24 * scale,
          fontSize: Math.max(8, 12 * scale),
          color: tone.text
        }} data-id="element-97" data-mp="2alauns8ux1hg">
            <span data-id="element-98" data-mp="1mw83ntsc1waa">9:41</span>
            <span className="tracking-[.2em]" data-id="element-99" data-mp="1mvzpzzodymy1">●◒▰</span>
          </div>
          <div ref={stageRef} aria-hidden="true" className="live-preview phone-scroll pointer-events-none absolute left-0 top-0 flex flex-col overflow-hidden" style={{
          width: SCREEN_WIDTH,
          height: SCREEN_HEIGHT,
          background: tone.surface,
          transform: `scale(${scale})`,
          transformOrigin: 'top left'
        }} data-id="element-100" data-mp="1rnzxcd6sci4n">
            <ScreenRouter initialPath={path} data-id="element-101" data-mp="17yz4wf0p9q8j"><ScreenView data-id="element-102" data-mp="10wau1j1nlane" /></ScreenRouter>
          </div>
        </div>
      </div>
    </figure>;
}